/**
 * Pure draft logic: the tiles a player has put on the board this turn (not yet
 * submitted) and the player's local rack order. No React, no stores — every
 * function takes plain data and returns new data, so it is unit-tested
 * directly (draft.test.ts).
 */
import type { Board, Coord, GameView, Tile } from '@wordloom/engine';

/** A tile placed on the board this turn (uncommitted). */
export interface DraftPlacement {
  tileId: string;
  row: number;
  col: number;
  /** Chosen letter — blanks only. */
  letter?: string;
}

export const cellKey = (row: number, col: number): string => `${row},${col}`;

// ---------------------------------------------------------------------------
// Rack order
// ---------------------------------------------------------------------------

/**
 * Merge a locally stored rack order with the server's rack: ids that are
 * still on the rack keep their local position, played/exchanged ids drop out,
 * and newly drawn ids are appended (in server order) and reported as `added`
 * so the UI can animate them in.
 */
export function orderRack(stored: readonly string[], rack: readonly Tile[]): { order: string[]; added: string[] } {
  const onRack = new Set(rack.map((t) => t.id));
  const seen = new Set<string>();
  const order: string[] = [];
  for (const id of stored) {
    if (onRack.has(id) && !seen.has(id)) {
      order.push(id);
      seen.add(id);
    }
  }
  const added: string[] = [];
  for (const t of rack) {
    if (!seen.has(t.id)) {
      order.push(t.id);
      added.push(t.id);
      seen.add(t.id);
    }
  }
  return { order, added };
}

/** Move `id` to `toIndex` (clamped) within `order`. */
export function moveInOrder(order: readonly string[], id: string, toIndex: number): string[] {
  const from = order.indexOf(id);
  if (from === -1) return [...order];
  const next = order.filter((x) => x !== id);
  const index = Math.max(0, Math.min(toIndex, next.length));
  next.splice(index, 0, id);
  return next;
}

/** Swap two ids in `order` (no-op if either is missing). */
export function swapInOrder(order: readonly string[], a: string, b: string): string[] {
  const ia = order.indexOf(a);
  const ib = order.indexOf(b);
  const next = [...order];
  if (ia === -1 || ib === -1 || ia === ib) return next;
  next[ia] = b;
  next[ib] = a;
  return next;
}

/**
 * Fisher–Yates shuffle of the ids in `subset` (the tiles visible on the rack),
 * keeping every other id in its slot. Guarantees a visibly different order
 * whenever that is possible (2+ distinct tiles).
 */
export function shuffleOrder(
  order: readonly string[],
  subset: ReadonlySet<string>,
  rng: () => number = Math.random,
): string[] {
  const positions: number[] = [];
  const ids: string[] = [];
  order.forEach((id, i) => {
    if (subset.has(id)) {
      positions.push(i);
      ids.push(id);
    }
  });
  if (ids.length < 2) return [...order];
  let shuffled = ids;
  for (let attempt = 0; attempt < 8; attempt++) {
    shuffled = [...ids];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = shuffled[i]!;
      shuffled[i] = shuffled[j]!;
      shuffled[j] = tmp;
    }
    if (shuffled.some((id, i) => id !== ids[i])) break;
  }
  // Deterministic fallback when the rng kept returning the identity: rotate.
  if (shuffled.every((id, i) => id === ids[i])) shuffled = [...ids.slice(1), ids[0]!];
  const next = [...order];
  positions.forEach((pos, k) => {
    next[pos] = shuffled[k]!;
  });
  return next;
}

/**
 * Move `tileId` so that it sits at `visibleIndex` among the tiles visible on
 * the rack (i.e. not placed on the board), keeping placed tiles' slots in the
 * full order untouched. Used for drag-reorder and drag-recall to a slot.
 */
export function insertAtVisibleIndex(
  order: readonly string[],
  placements: readonly DraftPlacement[],
  tileId: string,
  visibleIndex: number,
): string[] {
  const placed = new Set(placements.map((p) => p.tileId));
  placed.delete(tileId);
  const without = order.filter((id) => id !== tileId);
  const visible = without.filter((id) => !placed.has(id));
  const anchor = visible[Math.max(0, visibleIndex)];
  const at = anchor ? without.indexOf(anchor) : without.length;
  return [...without.slice(0, at), tileId, ...without.slice(at)];
}

/** Rack ids (in local order) that are not currently placed on the board. */
export function visibleRackIds(order: readonly string[], placements: readonly DraftPlacement[]): string[] {
  const placed = new Set(placements.map((p) => p.tileId));
  return order.filter((id) => !placed.has(id));
}

// ---------------------------------------------------------------------------
// Placements
// ---------------------------------------------------------------------------

/** Whether a square is free for a draft tile (no committed tile, no other draft tile). */
export function isSquareFree(
  board: Board,
  placements: readonly DraftPlacement[],
  row: number,
  col: number,
  ignoreTileId?: string,
): boolean {
  if (board[row]?.[col] !== null) return false;
  return !placements.some((p) => p.row === row && p.col === col && p.tileId !== ignoreTileId);
}

/**
 * Put (or move) a tile onto a square. Returns the new placement list, or null
 * when the square is taken. Moving an already-placed tile keeps its letter
 * (blank) unless a new one is given, and moves it to the END of the list so
 * Backspace recalls the most recently touched tile.
 */
export function placeTile(
  board: Board,
  placements: readonly DraftPlacement[],
  next: DraftPlacement,
): DraftPlacement[] | null {
  if (!isSquareFree(board, placements, next.row, next.col, next.tileId)) return null;
  const previous = placements.find((p) => p.tileId === next.tileId);
  const letter = next.letter ?? previous?.letter;
  const placed: DraftPlacement = { tileId: next.tileId, row: next.row, col: next.col };
  if (letter) placed.letter = letter;
  return [...placements.filter((p) => p.tileId !== next.tileId), placed];
}

export function recallTile(placements: readonly DraftPlacement[], tileId: string): DraftPlacement[] {
  return placements.filter((p) => p.tileId !== tileId);
}

export function placementAt(placements: readonly DraftPlacement[], row: number, col: number): DraftPlacement | undefined {
  return placements.find((p) => p.row === row && p.col === col);
}

// ---------------------------------------------------------------------------
// Reconciliation with a new server view
// ---------------------------------------------------------------------------

export interface ReconcileResult {
  placements: DraftPlacement[];
  rackOrder: string[];
  /** Draft tiles bumped because their square is now occupied (toast these). */
  returned: DraftPlacement[];
  /** Draft tiles dropped because the tile left the rack (e.g. it was just played). */
  dropped: DraftPlacement[];
  /** Newly drawn tile ids (animate in). */
  added: string[];
}

/**
 * Reconcile the local draft with a fresh GameView:
 *  - a placement whose tile is no longer on my rack is dropped silently (it
 *    was played/exchanged);
 *  - a placement whose square is now occupied on the board (the opponent's
 *    move landed there) returns to the rack and is reported in `returned`;
 *  - a blank's chosen letter is preserved;
 *  - the rack order keeps local positions, removes gone tiles and appends new
 *    ones (`added`).
 */
export function reconcileDraft(
  state: { placements: readonly DraftPlacement[]; rackOrder: readonly string[] },
  game: Pick<GameView, 'board' | 'rack'>,
): ReconcileResult {
  const rackIds = new Set(game.rack.map((t) => t.id));
  const placements: DraftPlacement[] = [];
  const returned: DraftPlacement[] = [];
  const dropped: DraftPlacement[] = [];
  const taken = new Set<string>();
  for (const p of state.placements) {
    if (!rackIds.has(p.tileId)) {
      dropped.push(p);
      continue;
    }
    const key = cellKey(p.row, p.col);
    if (game.board[p.row]?.[p.col] != null || taken.has(key)) {
      returned.push(p);
      continue;
    }
    taken.add(key);
    placements.push(p);
  }
  const { order, added } = orderRack(state.rackOrder, game.rack);
  return { placements, rackOrder: order, returned, dropped, added };
}

/** Coordinates of the draft (for highlighting). */
export function draftCoords(placements: readonly DraftPlacement[]): Coord[] {
  return placements.map((p) => ({ row: p.row, col: p.col }));
}
